export interface CropRect { x: number; y: number; width: number; height: number }
export type CropHandle = 'move' | 'nw' | 'ne' | 'sw' | 'se';
export const FULL_CROP: CropRect = { x: 0, y: 0, width: 1, height: 1 };
const clamp = (n: number, low: number, high: number) => Math.max(low, Math.min(high, n));

/** Fractions refer to the normalized source image, independent of preview size. */
export function constrainCrop(rect: CropRect, sourceWidth: number, sourceHeight: number): CropRect {
  const width = clamp(rect.width, 1 / sourceWidth, 1), height = clamp(rect.height, 1 / sourceHeight, 1);
  return { x: clamp(rect.x, 0, 1 - width), y: clamp(rect.y, 0, 1 - height), width, height };
}
export function dragCrop(start: CropRect, handle: CropHandle, dx: number, dy: number, sourceWidth: number, sourceHeight: number): CropRect {
  if (handle === 'move') return constrainCrop({ ...start, x: start.x + dx, y: start.y + dy }, sourceWidth, sourceHeight);
  const left = handle.endsWith('w') ? clamp(start.x + dx, 0, start.x + start.width - 1 / sourceWidth) : start.x;
  const right = handle.endsWith('e') ? clamp(start.x + start.width + dx, start.x + 1 / sourceWidth, 1) : start.x + start.width;
  const top = handle.startsWith('n') ? clamp(start.y + dy, 0, start.y + start.height - 1 / sourceHeight) : start.y;
  const bottom = handle.startsWith('s') ? clamp(start.y + start.height + dy, start.y + 1 / sourceHeight, 1) : start.y + start.height;
  return { x: left, y: top, width: right - left, height: bottom - top };
}
export function cropPixels(rect: CropRect, width: number, height: number): CropRect {
  const safe = constrainCrop(rect, width, height), x = Math.min(width - 1, Math.round(safe.x * width)), y = Math.min(height - 1, Math.round(safe.y * height));
  const right = clamp(Math.round((safe.x + safe.width) * width), x + 1, width);
  const bottom = clamp(Math.round((safe.y + safe.height) * height), y + 1, height);
  return { x, y, width: right - x, height: bottom - y };
}
export function squareCrop(width: number, height: number): CropRect {
  const edge = Math.min(width, height);
  return { x: (width - edge) / (2 * width), y: (height - edge) / (2 * height), width: edge / width, height: edge / height };
}

/** Canvas starts transparent; no matte is added to PNGs or foreground cutouts. */
export async function rasterizeCrop(image: HTMLImageElement, rect: CropRect): Promise<Blob> {
  if (!image.complete || !image.naturalWidth || !image.naturalHeight) throw new Error('The image is still loading.');
  const region = cropPixels(rect, image.naturalWidth, image.naturalHeight), canvas = document.createElement('canvas');
  canvas.width = region.width; canvas.height = region.height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Cropping is unavailable in this browser.');
  context.drawImage(image, region.x, region.y, region.width, region.height, 0, 0, region.width, region.height);
  return new Promise((resolve, reject) => canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('The crop could not be made.')), 'image/png'));
}
